import { useEffect, useState, useCallback } from "react";

/**
 * Wraps the browser's `beforeinstallprompt` event so any component can
 * offer an "Install app" button. Also detects when the app is already
 * running in standalone (PWA) mode so we can hide the button.
 */
export function usePWAInstall() {
  const [promptEvent, setPromptEvent] = useState(null);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    // Already-installed detection (Chrome/Android + iOS Safari)
    const mm = window.matchMedia("(display-mode: standalone)");
    const iosStandalone = typeof window.navigator.standalone === "boolean" && window.navigator.standalone;
    if (mm.matches || iosStandalone) setInstalled(true);

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setPromptEvent(e);
    };
    const onAppInstalled = () => { setInstalled(true); setPromptEvent(null); };

    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onAppInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onAppInstalled);
    };
  }, []);

  const install = useCallback(async () => {
    if (!promptEvent) return null;
    promptEvent.prompt();
    const { outcome } = await promptEvent.userChoice;
    setPromptEvent(null);
    return outcome; // "accepted" | "dismissed"
  }, [promptEvent]);

  return {
    canInstall: !!promptEvent && !installed,
    isInstalled: installed,
    install,
  };
}
